/* eslint-disable camelcase */
import * as React from 'react';

import { Table, Tbody, Td, Tr } from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { IconLockClosed } from '@lightspeed/flame/Icon/LockClosed';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { AnalyticsPromoModalLink } from 'root/apps/Analytics/AnalyticsPromoModalLink';
import { usePromoClicked } from 'root/apps/helpers/analyticsPromoUtils';

import { Shop } from '../utils/types';
import { LoadError } from './LoadError';
import { ForecastTdLight, OverlayDiv } from './ReorderPoints.style';

type Props = {
  shop: Shop;
  index: number;
  rowCount: number;
  hasAnalytics: boolean;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
};

const noDataToolTipText = 'No data to display';

/**
 * The SuggestedCells component fills out a row with suggested reorder points.
 * If the customer is not subscribed to analytics, it fillows the rows with a subscribe CTA.
 * If the suggested data is loading, it shows a Bone loading state.
 * If the suggested data fails to load, it shows an error and reload CTA.
 * @param shop The shop containing the suggested reorder data
 * @param index The index of the row being filled
 * @param rowCount The total number of shops
 * @param hasAnalytics Whether the customer is subscribed to Analytics
 * @param isLoading Whether the data is loading still
 * @param isError Whether the data failed to load
 * @param onReload A callback to reload the data on error
 * @returns
 */
export const SuggestedCells: React.FC<Props> = ({
  shop,
  index,
  rowCount,
  hasAnalytics,
  isLoading,
  isError,
  onReload,
}) => {
  const { hasBeenClicked } = usePromoClicked();

  if (!hasAnalytics) {
    if (index !== 0) {
      return <></>;
    }

    return (
      <ForecastTdLight
        colSpan={3}
        rowSpan={rowCount}
        style={{
          padding: 0,
          position: 'relative',
          borderLeft: 'solid 1px #D7DCE1',
        }}
      >
        <Table style={{ height: '100%' }}>
          <Tbody>
            {Array.from({ length: rowCount }, (_, i) => (
              <Tr key={`fake_row_${i}`}>
                <Td textAlign="right">
                  <var>7</var>
                </Td>
                <Td textAlign="right">
                  <var>7</var>
                </Td>
                <Td textAlign="right">
                  <var>7</var>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <OverlayDiv>
          <div>
            <IconLockClosed />
            {!hasBeenClicked ? (
              <>
                <Text mt={2}>Unlock the power of your data with Lightspeed Analytics.</Text>
                <Text>
                  <AnalyticsPromoModalLink>Learn more</AnalyticsPromoModalLink>
                </Text>
              </>
            ) : (
              <Text mt={2} style={{ maxWidth: '210px' }}>
                An account manager will be reaching out to you by email soon.
              </Text>
            )}
          </div>
        </OverlayDiv>
      </ForecastTdLight>
    );
  }

  if (isLoading) {
    return (
      <>
        <ForecastTdLight
          style={{
            borderLeft: 'solid 1px #D7DCE1',
          }}
        >
          <Bone />
        </ForecastTdLight>
        <ForecastTdLight>
          <Bone />
        </ForecastTdLight>
        <ForecastTdLight>
          <Bone />
        </ForecastTdLight>
      </>
    );
  }

  if (isError) {
    if (index !== 0) {
      return <></>;
    }

    return (
      <Td
        colSpan={3}
        rowSpan={rowCount}
        style={{
          padding: 0,
          position: 'relative',
          borderLeft: 'solid 1px #d7dce1',
        }}
      >
        <OverlayDiv>
          <LoadError rowCount={rowCount} onReload={onReload} />
        </OverlayDiv>
      </Td>
    );
  }

  return (
    <>
      <ForecastTdLight
        style={{
          borderLeft: 'solid 1px #D7DCE1',
        }}
      >
        {shop.dynamic_reorder_point ? (
          <var>{shop.dynamic_reorder_point}</var>
        ) : (
          <Tooltip placement="bottom-start" content={noDataToolTipText}>
            -
          </Tooltip>
        )}
      </ForecastTdLight>
      <ForecastTdLight>
        {shop.dynamic_reorder_level ? (
          <var>{shop.dynamic_reorder_level}</var>
        ) : (
          <Tooltip placement="bottom-start" content={noDataToolTipText}>
            -
          </Tooltip>
        )}
      </ForecastTdLight>
      <ForecastTdLight>
        <var>{shop.dynamic_need}</var>
      </ForecastTdLight>
    </>
  );
};
